// client/src/pages/Dashboard.tsx
import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { Link, useNavigate } from 'react-router-dom';
import {
    TrendingUp,
    ClipboardList,
    Store,
    Users,
    CheckCircle2,
    Clock,
    AlertCircle,
    Eye,
    Pencil,
    Image,
    RefreshCw,
    FileDown,
    FileSpreadsheet,
    Bell,
    Search,
    Filter,
    ArrowUp,
    ArrowDown
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { getJobCards } from '../services/jobcardApi';
import { getStores } from '../services/storeApi';
import { getUsers } from '../services/userApi';
import { JobCard } from '../types/jobcard';
import { AxiosResponse, AxiosError } from 'axios';
import api from '../services/api';
import { showSuccessToast, showErrorToast } from '../utils/toast';
import SystemHealthIndicator, { updateSyncStatus } from '../components/SystemHealthIndicator';
import notificationService from '../services/notificationService';
import { getManagerStats, TechnicianPerformance, DateRange } from '../services/dashboardApi';

type JobCardsResponse = AxiosResponse<{
    success: boolean;
    data: JobCard[] | {
        data: JobCard[];
        current_page?: number;
        last_page?: number;
        total?: number;
    };
}>;

const FILTER_STORAGE_KEY = 'jobCardsFilters';
const DEPT_OVERVIEW_FILTERS_KEY = 'deptOverviewFilters';

interface ManagerStats {
    technicianPerformance: TechnicianPerformance[];
}

export default function Dashboard() {
    const navigate = useNavigate();
    const { user } = useSelector((state: RootState) => state.auth);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [stats, setStats] = useState({
        totalJobCards: 0,
        openJobCards: 0,
        closedJobCards: 0,
        totalStores: 0,
        totalUsers: 0,
        recentJobCards: [] as JobCard[]
    });
    const [managerStats, setManagerStats] = useState<ManagerStats | null>(null);
    const [loadingManagerStats, setLoadingManagerStats] = useState(false);
    // Load persisted filters from localStorage
    const loadPersistedFilters = () => {
        try {
            const saved = localStorage.getItem(DEPT_OVERVIEW_FILTERS_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    statusFilter: parsed.statusFilter || 'all',
                    performanceRange: parsed.performanceRange || 'all',
                    field: parsed.field || 'completion_percentage',
                    direction: parsed.direction || 'desc',
                    dateRange: parsed.dateRange || 'last_3_months',
                    useCustomRange: parsed.useCustomRange || false,
                    customDateRange: parsed.customDateRange || { startDate: '', endDate: '' }
                };
            }
        } catch (error) {
            console.warn('Failed to load persisted department overview filters:', error);
        }
        return {
            statusFilter: 'all',
            performanceRange: 'all',
            field: 'completion_percentage',
            direction: 'desc',
            dateRange: 'last_3_months',
            useCustomRange: false,
            customDateRange: { startDate: '', endDate: '' }
        };
    };

    // Save filters to localStorage (excluding search term)
    const saveFiltersToStorage = (filters: typeof deptOverviewFilters, sorting: typeof deptOverviewSorting, dateRangeData?: { dateRange: DateRange, useCustomRange: boolean, customDateRange: { startDate: string, endDate: string } }) => {
        try {
            const toSave = {
                statusFilter: filters.statusFilter,
                performanceRange: filters.performanceRange,
                field: sorting.field,
                direction: sorting.direction,
                ...(dateRangeData && {
                    dateRange: dateRangeData.dateRange,
                    useCustomRange: dateRangeData.useCustomRange,
                    customDateRange: dateRangeData.customDateRange
                })
            };
            localStorage.setItem(DEPT_OVERVIEW_FILTERS_KEY, JSON.stringify(toSave));
        } catch (error) {
            console.warn('Failed to save department overview filters:', error);
        }
    };

    // Initialize state with persisted values
    const persistedFilters = loadPersistedFilters();
    
    // Update date range state with persisted values
    const [dateRange, setDateRangeState] = useState<DateRange>(persistedFilters.dateRange);
    const [customDateRange, setCustomDateRangeState] = useState(persistedFilters.customDateRange);
    const [useCustomRange, setUseCustomRangeState] = useState(persistedFilters.useCustomRange);
    
    // Department Overview filters and sorting state
    const [deptOverviewFilters, setDeptOverviewFiltersState] = useState({
        searchTerm: '', // Never persisted - always starts empty
        statusFilter: persistedFilters.statusFilter,
        performanceRange: persistedFilters.performanceRange
    });
    const [deptOverviewSorting, setDeptOverviewSortingState] = useState({
        field: persistedFilters.field,
        direction: persistedFilters.direction
    });

    // Wrapper functions that update state and persist to localStorage
    const setDeptOverviewFilters = (updater: React.SetStateAction<typeof deptOverviewFilters>) => {
        setDeptOverviewFiltersState(prev => {
            const newFilters = typeof updater === 'function' ? updater(prev) : updater;
            // Save to localStorage (excluding search term)
            saveFiltersToStorage(newFilters, deptOverviewSorting);
            return newFilters;
        });
    };

    const setDeptOverviewSorting = (updater: React.SetStateAction<typeof deptOverviewSorting>) => {
        setDeptOverviewSortingState(prev => {
            const newSorting = typeof updater === 'function' ? updater(prev) : updater;
            // Save to localStorage
            saveFiltersToStorage(deptOverviewFilters, newSorting);
            return newSorting;
        });
    };

    // Wrapper functions for date range that also persist to localStorage
    const setDateRange = (updater: React.SetStateAction<DateRange>) => {
        setDateRangeState(prev => {
            const newDateRange = typeof updater === 'function' ? updater(prev) : updater;
            // Save to localStorage
            saveFiltersToStorage(deptOverviewFilters, deptOverviewSorting, {
                dateRange: newDateRange,
                useCustomRange,
                customDateRange
            });
            return newDateRange;
        });
    };

    const setCustomDateRange = (updater: React.SetStateAction<{ startDate: string, endDate: string }>) => {
        setCustomDateRangeState((prev: { startDate: string, endDate: string }) => {
            const newCustomDateRange = typeof updater === 'function' ? updater(prev) : updater;
            // Save to localStorage
            saveFiltersToStorage(deptOverviewFilters, deptOverviewSorting, {
                dateRange,
                useCustomRange,
                customDateRange: newCustomDateRange
            });
            return newCustomDateRange;
        });
    };

    const setUseCustomRange = (updater: React.SetStateAction<boolean>) => {
        setUseCustomRangeState((prev: boolean) => {
            const newUseCustomRange = typeof updater === 'function' ? updater(prev) : updater;
            // Save to localStorage
            saveFiltersToStorage(deptOverviewFilters, deptOverviewSorting, {
                dateRange,
                useCustomRange: newUseCustomRange,
                customDateRange
            });
            return newUseCustomRange;
        });
    };

    // Export helpers for manager leaderboard
    // These functions generate CSV and Excel files from the current managerStats. The
    // data includes each technician's name, open/closed/total counts, and completion percentage.
    const generateLeaderboardRows = () => {
        // Guard: only proceed if stats are present
        if (!managerStats) return [] as Array<Record<string, string | number>>;

        return getFilteredAndSortedTechnicians().map(tech => {
            const total = tech.open + tech.closed;
            const completionPct = total > 0 ? Number(((tech.closed / total) * 100).toFixed(1)) : 0;
            return {
                Name: tech.user.name,
                Open: tech.open,
                Closed: tech.closed,
                Total: total,
                'Completion %': completionPct,
            };
        });
    };

    const getLeaderboardFilename = (ext: 'csv' | 'xlsx') => {
        const department = user?.position?.department?.name || 'Department';
        const date = new Date().toISOString().slice(0, 10);
        
        // Create a descriptive date range string for the filename
        let dateRangeString: string = dateRange;
        if (useCustomRange && customDateRange.startDate && customDateRange.endDate) {
            const startDate = new Date(customDateRange.startDate).toISOString().slice(0, 10);
            const endDate = new Date(customDateRange.endDate).toISOString().slice(0, 10);
            dateRangeString = `custom_${startDate}_to_${endDate}`;
        }
        
        return `${department.replace(/\s+/g, '_')}_leaderboard_${dateRangeString}_${date}.${ext}`;
    };

    const exportLeaderboardCSV = () => {
        // Build CSV content with a UTF-8 BOM for Excel compatibility on Windows
        const rows = generateLeaderboardRows();
        const headers = ['Name', 'Open', 'Closed', 'Total', 'Completion %'];
        const csvLines = [headers.join(',')];
        rows.forEach(row => {
            const line = headers.map(h => {
                const value = String(row[h as keyof typeof row] ?? '');
                // Escape commas, quotes, and newlines per RFC 4180
                const escaped = value.replace(/"/g, '""');
                return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
            }).join(',');
            csvLines.push(line);
        });
        const csvContent = '\uFEFF' + csvLines.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = getLeaderboardFilename('csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const exportLeaderboardExcel = () => {
        // Create an XLSX workbook from the same rows
        const rows = generateLeaderboardRows();
        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Leaderboard');
        XLSX.writeFile(workbook, getLeaderboardFilename('xlsx'));
    };

    // Fetch dashboard data with accurate counts
    // Note: We make separate API calls for total, open, and closed job cards to get precise counts
    // instead of using ratio calculations which can be inaccurate due to:
    // 1. Pagination limits (e.g., requesting 1000 but getting only a subset)
    // 2. Non-uniform data distribution across pages
    // 3. User permission filtering (non-admin users only see their own job cards)
    // This ensures dashboard counts match the job cards page filtered results
    const fetchDashboardData = useCallback(async () => {
        try {
            setLoading(true);

            // Check if user is a manager and get their department's user IDs
            const isManager = user?.position?.name === 'Manager';
            let departmentUserIds: number[] | undefined = undefined;

            if (isManager) {
                // Initial fetch for manager stats on component load
                // fetchManagerStats(); // Moved to useEffect
                
                const allUsersRes = await getUsers();
                const allUsers = allUsersRes.data || [];
                const departmentUsers = allUsers.filter(u => u.position?.department?.id === user.position?.department?.id);
                departmentUserIds = departmentUsers.map(u => u.id);

                // If no users found in department, default to manager's own ID
                if (departmentUserIds.length === 0 && user.id) {
                    departmentUserIds = [user.id];
                }
            }

            // Get accurate counts by making separate calls for each status
            const [allJobCardsRes, openJobCardsRes, closedJobCardsRes, recentJobCardsRes, storesRes, usersRes] = await Promise.all([
                getJobCards('', undefined, 1, { user_id: departmentUserIds }) as Promise<JobCardsResponse>,  // Get total count (just need pagination info)
                getJobCards('', false, 1, { user_id: departmentUserIds }) as Promise<JobCardsResponse>,      // Get open job cards count
                getJobCards('', true, 1, { user_id: departmentUserIds }) as Promise<JobCardsResponse>,       // Get closed job cards count
                getJobCards('', undefined, 5, { user_id: departmentUserIds }) as Promise<JobCardsResponse>,  // Get 5 recent ones for display
                getStores(),
                getUsers()
            ]);

            // Extract accurate counts from pagination data
            let totalJobCards = 0;
            let totalOpenJobCards = 0;
            let totalClosedJobCards = 0;

            // Get total count
            const allJobCardsData = allJobCardsRes.data.data;
            if (allJobCardsData && typeof allJobCardsData === 'object' && 'total' in allJobCardsData) {
                totalJobCards = allJobCardsData.total || 0;
            } else if (Array.isArray(allJobCardsData)) {
                totalJobCards = allJobCardsData.length;
            }

            // Get open count
            const openJobCardsData = openJobCardsRes.data.data;
            if (openJobCardsData && typeof openJobCardsData === 'object' && 'total' in openJobCardsData) {
                totalOpenJobCards = openJobCardsData.total || 0;
            } else if (Array.isArray(openJobCardsData)) {
                totalOpenJobCards = openJobCardsData.length;
            }

            // Get closed count
            const closedJobCardsData = closedJobCardsRes.data.data;
            if (closedJobCardsData && typeof closedJobCardsData === 'object' && 'total' in closedJobCardsData) {
                totalClosedJobCards = closedJobCardsData.total || 0;
            } else if (Array.isArray(closedJobCardsData)) {
                totalClosedJobCards = closedJobCardsData.length;
            }

            // Process recent job cards for display
            const recentJobCardsData = recentJobCardsRes.data.data;
            let recentJobCards: JobCard[] = [];
            
            if (recentJobCardsData && typeof recentJobCardsData === 'object' && 'data' in recentJobCardsData) {
                recentJobCards = recentJobCardsData.data;
            } else if (Array.isArray(recentJobCardsData)) {
                recentJobCards = recentJobCardsData;
            }

            const stores = storesRes.data?.data || [];
            const users = usersRes.data || [];

            setStats(prevStats => ({
                ...prevStats,
                totalJobCards: totalJobCards,
                openJobCards: totalOpenJobCards,
                closedJobCards: totalClosedJobCards,
                totalStores: stores.length,
                totalUsers: users.length,
                recentJobCards: recentJobCards
            }));

        } catch {
            // Silently handle errors
        } finally {
            setLoading(false);
        }
    }, [user]);

    const fetchManagerStats = useCallback(async () => {
        if (!user || user.position?.name !== 'Manager' || !user.position.department?.id) {
            return;
        }

        setLoadingManagerStats(true);
        try {
            const allUsersRes = await getUsers();
            const departmentUsers = allUsersRes.data.filter(u => u.position?.department?.id === user.position?.department?.id);
            const departmentUserIds = departmentUsers.map(u => u.id);

            if (departmentUserIds.length > 0) {
                // Use custom date range if enabled and both dates are provided
                const dateRangeToUse = useCustomRange && customDateRange.startDate && customDateRange.endDate 
                    ? { start_date: customDateRange.startDate, end_date: customDateRange.endDate }
                    : dateRange;

                const response = await getManagerStats(departmentUserIds, dateRangeToUse);
                setManagerStats({
                    technicianPerformance: response.data.technician_performance,
                });
            }
        } catch {
            showErrorToast("Failed to load manager statistics");
        } finally {
            setLoadingManagerStats(false);
        }
    }, [user, dateRange, customDateRange, useCustomRange]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    useEffect(() => {
        // Refetch manager stats when date range or custom date range changes
        if (user?.position?.name === 'Manager') {
            fetchManagerStats();
        }
    }, [dateRange, useCustomRange, user, fetchManagerStats]);

    const handleMigrateJobPictures = async () => {
        try {
            const response = await api.post('/admin/migrate-job-pictures');
            if (response.data.success) {
                showSuccessToast('Job pictures migration completed successfully');
            } else {
                showErrorToast('Migration failed: ' + response.data.message);
            }
        } catch (error: unknown) {
            const apiError = error as { response?: { data?: { message?: string } }, message?: string };
            showErrorToast('Migration failed: ' + (apiError.response?.data?.message || apiError.message));
        }
    };

    // Function to sync remote data with local database
    const handleSyncRemoteData = async () => {
        try {
            setSyncing(true);
            const response = await api.post('/system/sync-all');
            if (response.data.success) {
                showSuccessToast('Remote data synchronized successfully');
                // Update sync status in localStorage
                updateSyncStatus(true);
                // Refresh dashboard data to show updated counts
                await fetchDashboardData();
            } else {
                showErrorToast('Sync completed with errors. Check server logs for details.');
                updateSyncStatus(false);
            }
        } catch (error) {
            const axiosError = error as AxiosError;
            const errorMessage = axiosError.response?.data && typeof axiosError.response.data === 'object' && 'message' in axiosError.response.data 
                ? axiosError.response.data.message as string 
                : axiosError.message;
            showErrorToast('Sync failed: ' + errorMessage);
            updateSyncStatus(false);
        } finally {
            setSyncing(false);
        }
    };

    // Function to navigate to job cards with specific filters
    const navigateToJobCards = (statusFilter: 'all' | 'open' | 'closed' = 'all') => {
        // Set filter values in localStorage
        const isManager = user?.position?.name === 'Manager';
        let userFilter: string | number[] = 'all';

        if (isManager && user?.position?.department?.id) {
            // This part is tricky because we don't have the full user list here.
            // A better approach might be to just navigate and let the jobcards page handle the filtering.
            // For now, we will clear user filters for managers to avoid incorrect data.
            userFilter = 'all'; 
        }

        const filters = {
            searchTerm: '',
            statusFilter,
            dateRangeFilter: {
                startDate: '',
                endDate: ''
            },
            storeFilter: 'all',
            userFilter: userFilter,
        };
        
        localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
        navigate('/jobcards');
    };

    // Function to convert predefined date range to actual dates
    const getDateRangeFromPredefined = (range: DateRange) => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        switch (range) {
            case 'this_week': {
                const startOfWeek = new Date(today);
                startOfWeek.setDate(today.getDate() - today.getDay());
                return {
                    startDate: startOfWeek.toISOString().split('T')[0],
                    endDate: today.toISOString().split('T')[0]
                };
            }
            case 'this_month':
                return {
                    startDate: new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0],
                    endDate: today.toISOString().split('T')[0]
                };
            case 'last_3_months': {
                const threeMonthsAgo = new Date(today);
                threeMonthsAgo.setMonth(today.getMonth() - 3);
                return {
                    startDate: threeMonthsAgo.toISOString().split('T')[0],
                    endDate: today.toISOString().split('T')[0]
                };
            }
            case 'last_6_months': {
                const sixMonthsAgo = new Date(today);
                sixMonthsAgo.setMonth(today.getMonth() - 6);
                return {
                    startDate: sixMonthsAgo.toISOString().split('T')[0],
                    endDate: today.toISOString().split('T')[0]
                };
            }
            case 'this_year':
                return {
                    startDate: new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0],
                    endDate: today.toISOString().split('T')[0]
                };
            case 'all_time':
                return {
                    startDate: '',
                    endDate: ''
                };
            default:
                return {
                    startDate: '',
                    endDate: ''
                };
        }
    };

    // Function to navigate to job cards with technician-specific filters
    const navigateToTechnicianJobCards = (technicianId: number) => {
        // Get actual start and end dates
        let actualStartDate = '';
        let actualEndDate = '';
        
        if (useCustomRange && customDateRange.startDate && customDateRange.endDate) {
            actualStartDate = customDateRange.startDate;
            actualEndDate = customDateRange.endDate;
        } else {
            const dateRangeResult = getDateRangeFromPredefined(dateRange);
            actualStartDate = dateRangeResult.startDate;
            actualEndDate = dateRangeResult.endDate;
        }

        // Prepare date range for URL parameters
        let dateRangeParams = '';
        if (actualStartDate && actualEndDate) {
            dateRangeParams = `?start_date=${actualStartDate}&end_date=${actualEndDate}&user_id=${technicianId}`;
        } else {
            dateRangeParams = `?user_id=${technicianId}`;
        }

        // Set filter values in localStorage with technician and date range
        const filters = {
            searchTerm: '',
            statusFilter: 'all' as const,
            dateRangeFilter: {
                startDate: actualStartDate,
                endDate: actualEndDate
            },
            storeFilter: 'all',
            userFilter: technicianId.toString(),
            departmentFilter: 'all'
        };
        
        localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
        navigate(`/jobcards${dateRangeParams}`);
    };

    // Filter and sort technician performance data
    const getFilteredAndSortedTechnicians = useCallback(() => {
        if (!managerStats?.technicianPerformance) return [];
        
        let filtered = [...managerStats.technicianPerformance];
        
        // Apply search filter
        if (deptOverviewFilters.searchTerm) {
            const searchLower = deptOverviewFilters.searchTerm.toLowerCase();
            filtered = filtered.filter(tech => 
                tech.user.name.toLowerCase().includes(searchLower)
            );
        }
        
        // Apply status filter
        if (deptOverviewFilters.statusFilter !== 'all') {
            filtered = filtered.filter(tech => {
                const total = tech.open + tech.closed;
                const completionPercentage = total > 0 ? (tech.closed / total) * 100 : 0;
                
                switch (deptOverviewFilters.statusFilter) {
                    case 'high_performers':
                        return completionPercentage >= 80;
                    case 'low_performers':
                        return completionPercentage < 50;
                    case 'open_heavy':
                        return tech.open > tech.closed;
                    case 'closed_heavy':
                        return tech.closed > tech.open;
                    default:
                        return true;
                }
            });
        }
        
        // Apply performance range filter
        if (deptOverviewFilters.performanceRange !== 'all') {
            // First sort by completion percentage to determine ranges
            const sortedByCompletion = [...filtered].sort((a, b) => {
                const aCompletion = a.open + a.closed > 0 ? (a.closed / (a.open + a.closed)) * 100 : 0;
                const bCompletion = b.open + b.closed > 0 ? (b.closed / (b.open + b.closed)) * 100 : 0;
                return bCompletion - aCompletion;
            });
            
            const total = sortedByCompletion.length;
            const top25Count = Math.ceil(total * 0.25);
            const bottom25Count = Math.floor(total * 0.25);
            
            switch (deptOverviewFilters.performanceRange) {
                case 'top_25':
                    filtered = sortedByCompletion.slice(0, top25Count);
                    break;
                case 'bottom_25':
                    filtered = sortedByCompletion.slice(-bottom25Count);
                    break;
                case 'middle_50':
                    filtered = sortedByCompletion.slice(top25Count, total - bottom25Count);
                    break;
            }
        }
        
        // Apply sorting
        filtered.sort((a, b) => {
            let aValue: number | string;
            let bValue: number | string;
            
            switch (deptOverviewSorting.field) {
                case 'name':
                    aValue = a.user.name.toLowerCase();
                    bValue = b.user.name.toLowerCase();
                    break;
                case 'completion_percentage':
                    aValue = a.open + a.closed > 0 ? (a.closed / (a.open + a.closed)) * 100 : 0;
                    bValue = b.open + b.closed > 0 ? (b.closed / (b.open + b.closed)) * 100 : 0;
                    break;
                case 'total_jobs':
                    aValue = a.open + a.closed;
                    bValue = b.open + b.closed;
                    break;
                case 'open_jobs':
                    aValue = a.open;
                    bValue = b.open;
                    break;
                case 'closed_jobs':
                    aValue = a.closed;
                    bValue = b.closed;
                    break;
                default:
                    aValue = 0;
                    bValue = 0;
            }
            
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return deptOverviewSorting.direction === 'asc' 
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            } else {
                return deptOverviewSorting.direction === 'asc' 
                    ? (aValue as number) - (bValue as number)
                    : (bValue as number) - (aValue as number);
            }
        });
        
        return filtered;
    }, [managerStats, deptOverviewFilters, deptOverviewSorting]);

    // Function to test push notifications
    const testNotification = async () => {
        try {
            // Check if notifications are supported
            if (!('Notification' in window)) {
                showErrorToast('This browser does not support notifications');
                return;
            }

            // Check permission status
            const permissionStatus = notificationService.getPermissionStatus();
            if (!permissionStatus.granted) {
                showErrorToast('Notification permission not granted. Please enable notifications first.');
                return;
            }

            // Send a test notification
            await notificationService.sendPushNotification({
                title: 'Test Notification',
                body: 'This is a test notification from the Job Card System!',
                icon: '/icons/icon-192x192.png',
                tag: 'test-notification',
                data: {
                    type: 'test',
                    timestamp: Date.now()
                }
            });

            showSuccessToast('Test notification sent successfully!');
        } catch (error) {
            console.error('Failed to send test notification:', error);
            showErrorToast('Failed to send test notification. Check console for details.');
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px]">
                <div className="w-12 h-12 border-4 border-gray-200 dark:border-gray-700 border-t-blue-500 dark:border-t-blue-400 rounded-full animate-spin mb-4"></div>
                <div className="text-gray-600 dark:text-gray-400">Loading dashboard data...</div>
                <div className="text-sm text-gray-500 dark:text-gray-500 mt-2">Fetching job card statistics and recent activity</div>
            </div>
        );
    }

    const completionRate = stats.totalJobCards > 0
        ? ((stats.closedJobCards / stats.totalJobCards) * 100).toFixed(1)
        : 0;

    return (
        <>
            <header className="mb-8">
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
                        <p className="text-gray-600 dark:text-gray-400">Welcome back, {user?.name}</p>
                    </div>
                    
                    {user?.utype === 'ADMIN' && (
                        <SystemHealthIndicator showDetails={true} className="bg-white dark:bg-gray-800" />
                    )}
                </div>
                
                {/* Test Notification Button - moved to its own row */}
                <div className="flex justify-start">
                    <button
                        onClick={testNotification}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                        title="Send a test notification"
                    >
                        <Bell className="w-4 h-4" />
                        Test Notification
                    </button>
                </div>
            </header>

            {/* Notification Permission Banner */}
            {!notificationService.hasPermission() && notificationService.isNotificationSupported() && (
                <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <Bell className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-3" />
                            <div>
                                <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                                    Enable Notifications
                                </h3>
                                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                                    Stay updated with job card notifications and reminders.
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => navigate('/settings')}
                            className="px-3 py-1.5 bg-yellow-600 hover:bg-yellow-700 text-white text-sm rounded-md transition-colors duration-200"
                        >
                            Enable
                        </button>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <DashboardCard
                    title="Total Job Cards"
                    value={stats.totalJobCards.toString()}
                    icon={<ClipboardList className="w-6 h-6 text-blue-600 dark:text-blue-400" />}
                    footer={`${completionRate}% completion rate`}
                    variant="blue"
                    onClick={() => navigateToJobCards('all')}
                />
                <DashboardCard
                    title="Open Job Cards"
                    value={stats.openJobCards.toString()}
                    icon={<Clock className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />}
                    footer="Require attention"
                    variant="yellow"
                    onClick={() => navigateToJobCards('open')}
                />
                <DashboardCard
                    title="Closed Job Cards"
                    value={stats.closedJobCards.toString()}
                    icon={<CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />}
                    footer="Completed jobs"
                    variant="green"
                    onClick={() => navigateToJobCards('closed')}
                />
                {user?.utype === 'ADMIN' && (
                    <>
                        <DashboardCard
                            title="Total Stores"
                            value={stats.totalStores.toString()}
                            icon={<Store className="w-6 h-6 text-green-600 dark:text-green-400" />}
                            footer="Active locations"
                            variant="green"
                            onClick={() => navigate('/stores')}
                        />
                        <DashboardCard
                            title="Total Users"
                            value={stats.totalUsers.toString()}
                            icon={<Users className="w-6 h-6 text-purple-600 dark:text-purple-400" />}
                            footer="System users"
                            variant="purple"
                            onClick={() => navigate('/users')}
                        />
                    </>
                )}
            </div>

            {user?.position?.name === 'Manager' && managerStats && (
                <div className="mb-8 p-6 bg-white dark:bg-gray-800 rounded-lg shadow">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                        {user.position.department?.name} Overview
                    </h2>
                    <div className="mb-6 space-y-4">
                        {/* Date Range and Filters Row */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {/* Predefined Date Range Selector */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Date Range
                                </label>
                                <div className="flex items-center gap-4">
                                    <select
                                        value={useCustomRange ? 'custom' : dateRange}
                                        onChange={(e) => {
                                            if (e.target.value === 'custom') {
                                                setUseCustomRange(true);
                                            } else {
                                                setUseCustomRange(false);
                                                setDateRange(e.target.value as DateRange);
                                            }
                                        }}
                                        className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-md shadow-sm px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    >
                                        <option value="this_week">This Week</option>
                                        <option value="this_month">This Month</option>
                                        <option value="last_3_months">Last 3 Months</option>
                                        <option value="last_6_months">Last 6 Months</option>
                                        <option value="this_year">This Year</option>
                                        <option value="all_time">All Time</option>
                                        <option value="custom">Custom Range</option>
                                    </select>
                                </div>
                            </div>

                            {/* Search Filter */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Search Technicians
                                </label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search by name..."
                                        value={deptOverviewFilters.searchTerm}
                                        onChange={(e) => setDeptOverviewFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
                                        className="w-full pl-10 pr-3 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Advanced Filters Row */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {/* Status Filter */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Performance Status
                                </label>
                                <select
                                    value={deptOverviewFilters.statusFilter}
                                    onChange={(e) => setDeptOverviewFilters(prev => ({ ...prev, statusFilter: e.target.value }))}
                                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-md shadow-sm px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                >
                                    <option value="all">All Technicians</option>
                                    <option value="high_performers">High Performers (≥80%)</option>
                                    <option value="low_performers">Low Performers (&lt;50%)</option>
                                    <option value="open_heavy">Open Heavy (More Open Jobs)</option>
                                    <option value="closed_heavy">Closed Heavy (More Closed Jobs)</option>
                                </select>
                            </div>

                            {/* Performance Range Filter */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Performance Range
                                </label>
                                <select
                                    value={deptOverviewFilters.performanceRange}
                                    onChange={(e) => setDeptOverviewFilters(prev => ({ ...prev, performanceRange: e.target.value }))}
                                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-md shadow-sm px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                >
                                    <option value="all">All Ranges</option>
                                    <option value="top_25">Top 25%</option>
                                    <option value="middle_50">Middle 50%</option>
                                    <option value="bottom_25">Bottom 25%</option>
                                </select>
                            </div>

                            {/* Sort Controls */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Sort By
                                </label>
                                <div className="flex gap-2">
                                    <select
                                        value={deptOverviewSorting.field}
                                        onChange={(e) => setDeptOverviewSorting(prev => ({ ...prev, field: e.target.value }))}
                                        className="flex-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-md shadow-sm px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    >
                                        <option value="completion_percentage">Completion %</option>
                                        <option value="name">Name</option>
                                        <option value="total_jobs">Total Jobs</option>
                                        <option value="open_jobs">Open Jobs</option>
                                        <option value="closed_jobs">Closed Jobs</option>
                                    </select>
                                    <button
                                        onClick={() => setDeptOverviewSorting(prev => ({ 
                                            ...prev, 
                                            direction: prev.direction === 'asc' ? 'desc' : 'asc' 
                                        }))}
                                        className="px-3 py-1.5 bg-gray-100 dark:bg-gray-600 border border-gray-300 dark:border-gray-500 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
                                        title={`Sort ${deptOverviewSorting.direction === 'asc' ? 'Descending' : 'Ascending'}`}
                                    >
                                        {deptOverviewSorting.direction === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Filter Summary and Clear Button */}
                        <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                                <span className="font-medium">Active Filters:</span>
                                {deptOverviewFilters.searchTerm && (
                                    <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 rounded-full text-xs">
                                        Search: "{deptOverviewFilters.searchTerm}"
                                    </span>
                                )}
                                {deptOverviewFilters.statusFilter !== 'all' && (
                                    <span className="px-2 py-1 bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 rounded-full text-xs">
                                        {deptOverviewFilters.statusFilter.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                    </span>
                                )}
                                {deptOverviewFilters.performanceRange !== 'all' && (
                                    <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 rounded-full text-xs">
                                        {deptOverviewFilters.performanceRange.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                    </span>
                                )}
                                <span className="px-2 py-1 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-full text-xs">
                                    Sort: {deptOverviewSorting.field.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())} ({deptOverviewSorting.direction === 'asc' ? 'A-Z' : 'Z-A'})
                                </span>
                                <span className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-300 rounded-full text-xs">
                                    Date: {useCustomRange ? 'Custom' : dateRange.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                </span>
                            </div>
                            <button
                                onClick={() => {
                                    // Clear localStorage
                                    localStorage.removeItem(DEPT_OVERVIEW_FILTERS_KEY);
                                    
                                    // Reset to default values
                                    setDeptOverviewFiltersState({
                                        searchTerm: '',
                                        statusFilter: 'all',
                                        performanceRange: 'all'
                                    });
                                    setDeptOverviewSortingState({
                                        field: 'completion_percentage',
                                        direction: 'desc'
                                    });
                                    setDateRangeState('last_3_months');
                                    setCustomDateRangeState({ startDate: '', endDate: '' });
                                    setUseCustomRangeState(false);
                                }}
                                className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                            >
                                Clear All Filters
                            </button>
                        </div>

                        {/* Custom Date Range Picker */}
                        {useCustomRange && (
                            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                    Custom Date Range
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                            Start Date
                                        </label>
                                        <input
                                            type="date"
                                            value={customDateRange.startDate}
                                            onChange={(e) => setCustomDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                                            className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-md shadow-sm px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                            End Date
                                        </label>
                                        <input
                                            type="date"
                                            value={customDateRange.endDate}
                                            onChange={(e) => setCustomDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                                            className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-md shadow-sm px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        />
                                    </div>
                                </div>
                                {customDateRange.startDate && customDateRange.endDate && (
                                    <div className="mt-3 text-xs text-gray-600 dark:text-gray-400">
                                        Showing data from {new Date(customDateRange.startDate).toLocaleDateString()} to {new Date(customDateRange.endDate).toLocaleDateString()}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {loadingManagerStats ? (
                        <div className="text-center py-8">Loading department stats...</div>
                    ) : (
                        <div className="grid grid-cols-1 gap-8">
                            <div>
                                <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-2 mb-3">
                                    <div>
                                        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">Leaderboard</h3>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            Showing {getFilteredAndSortedTechnicians().length} of {managerStats.technicianPerformance.length} technicians
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={exportLeaderboardCSV}
                                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                            title="Export CSV"
                                        >
                                            <FileDown className="w-4 h-4" />
                                            CSV
                                        </button>
                                        <button
                                            type="button"
                                            onClick={exportLeaderboardExcel}
                                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                            title="Export Excel"
                                        >
                                            <FileSpreadsheet className="w-4 h-4" />
                                            Excel
                                        </button>
                                    </div>
                                </div>
                                <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                                    {getFilteredAndSortedTechnicians().length === 0 ? (
                                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                            <Filter className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                            <p className="text-lg font-medium mb-2">No technicians found</p>
                                            <p className="text-sm">Try adjusting your filters or search terms</p>
                                        </div>
                                    ) : (
                                        getFilteredAndSortedTechnicians().map(tech => {
                                        const total = tech.open + tech.closed;
                                        const closedPercentage = total > 0 ? (tech.closed / total) * 100 : 0;
                                        const openPercentage = total > 0 ? 100 - closedPercentage : 0;

                                        return (
                                            <div key={tech.user.id}>
                                                <div 
                                                    className="flex justify-between items-center text-sm mb-1 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors duration-200"
                                                    onClick={() => navigateToTechnicianJobCards(tech.user.id)}
                                                    title={`Click to view ${tech.user.name}'s job cards for the selected date range`}
                                                >
                                                    <span className="font-medium text-gray-900 dark:text-white">{tech.user.name}</span>
                                                    <div className="flex items-center gap-4">
                                                        <span className="text-yellow-600 dark:text-yellow-400">Open: {tech.open}</span>
                                                        <span className="text-green-600 dark:text-green-400">Closed: {tech.closed}</span>
                                                        <span className="font-bold text-blue-600 dark:text-blue-400">Total: {total}</span>
                                                        <span className="font-semibold text-gray-700 dark:text-gray-300 text-xs">
                                                            ({closedPercentage.toFixed(0)}% Complete)
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="w-full flex h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" title={`Open: ${openPercentage.toFixed(0)}%, Closed: ${closedPercentage.toFixed(0)}%`}>
                                                    <div className="bg-green-500" style={{ width: `${closedPercentage}%` }}></div>
                                                    <div className="bg-yellow-500" style={{ width: `${openPercentage}%` }}></div>
                                                </div>
                                            </div>
                                        );
                                        })
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Job Cards</h2>
                        <Link to="/jobcards" className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                            View all
                        </Link>
                    </div>
                    <div className="space-y-0 divide-y divide-gray-200 dark:divide-gray-700">
                        {stats.recentJobCards.map((jobCard, index) => (
                            <div
                                key={jobCard.id}
                                className={`flex items-center justify-between p-4 ${index % 2 === 0
                                    ? 'bg-white dark:bg-gray-800'
                                    : 'bg-gray-50 dark:bg-gray-700'
                                    } hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors`}
                            >
                                <div className="flex items-center space-x-3">
                                    {jobCard.closed ? (
                                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                                    ) : (
                                        <AlertCircle className="w-5 h-5 text-yellow-500" />
                                    )}
                                    <div>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                                            {jobCard.job_number}
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {jobCard.user?.utype === 'STORE' ? jobCard.supplier?.name : jobCard.store?.name || 'Unknown Store'}
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {jobCard.user?.name || 'Unknown User'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="text-right">
                                        <p className="text-sm text-gray-900 dark:text-white">
                                            {jobCard.date ? new Date(jobCard.date).toLocaleDateString() : 'N/A'}
                                        </p>
                                        <span className={`text-xs px-2 py-1 rounded-full ${jobCard.closed
                                            ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-400'
                                            : 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-400'
                                            }`}>
                                            {jobCard.closed ? 'Closed' : 'Open'}
                                        </span>
                                        {jobCard.closed && jobCard.date_closed && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                Closed: {new Date(jobCard.date_closed).toLocaleDateString()}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex items-center">
                                        {jobCard.closed ? (
                                            <button
                                                onClick={() => navigate(`/jobcards/view/${jobCard.id}`)}
                                                className="p-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                                                title="View Job Card"
                                            >
                                                <Eye className="w-5 h-5" />
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => navigate(`/jobcards/edit/${jobCard.id}`)}
                                                className="p-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                                                title="Edit Job Card"
                                            >
                                                <Pencil className="w-5 h-5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h2>
                    <div className="grid grid-cols-2 gap-4">
                        <Link
                            to="/jobcards"
                            className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <ClipboardList className="w-6 h-6 text-blue-500 dark:text-blue-400 mb-2" />
                            <p className="text-sm font-medium text-gray-900 dark:text-white">Job Cards</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">View and manage jobs</p>
                        </Link>
                        {user?.utype === 'ADMIN' && (
                            <>
                                <Link
                                    to="/stores"
                                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <Store className="w-6 h-6 text-green-500 dark:text-green-400 mb-2" />
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">Stores</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Manage locations</p>
                                </Link>
                                <Link
                                    to="/users"
                                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <Users className="w-6 h-6 text-purple-500 dark:text-purple-400 mb-2" />
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">Users</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Manage team members</p>
                                </Link>
                                <Link
                                    to="#"
                                    onClick={handleMigrateJobPictures}
                                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <Image className="w-6 h-6 text-orange-500 dark:text-orange-400 mb-2" />
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">Migrate Pictures</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Move job pictures to new system</p>
                                </Link>
                                <Link
                                    to="#"
                                    onClick={syncing ? undefined : handleSyncRemoteData}
                                    className={`p-4 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors ${
                                        syncing 
                                            ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' 
                                            : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                                    }`}
                                >
                                    {syncing ? (
                                        <div className="flex flex-col items-center">
                                            <div className="w-6 h-6 border-2 border-t-cyan-500 border-cyan-500/20 rounded-full animate-spin mb-2"></div>
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">Syncing...</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Please wait</p>
                                        </div>
                                    ) : (
                                        <>
                                            <RefreshCw className="w-6 h-6 text-cyan-500 dark:text-cyan-400 mb-2" />
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">Sync Remote Data</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Update stores & equipment data</p>
                                        </>
                                    )}
                                </Link>
                            </>
                        )}
                        <Link
                            to="#"
                            onClick={fetchDashboardData}
                            className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <TrendingUp className="w-6 h-6 text-indigo-500 dark:text-indigo-400 mb-2" />
                            <p className="text-sm font-medium text-gray-900 dark:text-white">Refresh Data</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Update dashboard</p>
                        </Link>
                    </div>
                </div>
            </div>
        </>
    );
}

interface DashboardCardProps {
    title: string;
    value: string;
    icon: React.ReactNode;
    footer: string;
    variant?: 'blue' | 'yellow' | 'green' | 'purple';
    onClick?: () => void;
}

function DashboardCard({ title, value, icon, footer, variant = 'blue', onClick }: DashboardCardProps) {
    const variants = {
        blue: 'bg-blue-100/80 dark:bg-blue-900/50 border border-blue-200 dark:border-blue-800',
        yellow: 'bg-yellow-100/80 dark:bg-yellow-900/50 border border-yellow-200 dark:border-yellow-800',
        green: 'bg-green-100/80 dark:bg-green-900/50 border border-green-200 dark:border-green-800',
        purple: 'bg-purple-100/80 dark:bg-purple-900/50 border border-purple-200 dark:border-purple-800'
    };

    return (
        <div 
            className={`rounded-lg shadow-sm p-6 hover:shadow-md transition-all ${variants[variant]} ${onClick ? 'cursor-pointer hover:scale-105' : ''}`}
            onClick={onClick}
        >
            <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{title}</p>
                {icon}
            </div>
            <p className="text-3xl font-semibold text-gray-900 dark:text-white mb-4">{value}</p>
            <p className="text-sm text-gray-700 dark:text-gray-300">{footer}</p>
        </div>
    );
}